import { ArrowsDownload, Button, Header, Paragraph, ResponsiveButton, ShareIcon } from "@datagame/ui";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames/bind";

import { Section } from "../../project/ProjectView/ProjectSettings/Steps/Common";
import { SelectInput } from "../../shared/ui/SelectInput";
import { SelectOption } from "../../shared/ui/SelectInput/SelectInput";
import { GameForReport, GameReport } from "../GameReport";
import { ShareSection } from "./ShareSection";
import { ShareReportModal } from "../../shared/modals/ShareReportModal";
import { Project } from "../../../gql/graphql";
import { ReportBadge } from "../ReportBadge";

import styles from "./ReportView.module.scss";
import { MobileStickyBar } from "../../shared/MobileStickyBar";

const cx = classNames.bind(styles);

const API_HOST = import.meta.env.VITE_API_HOST;

export type ProjectForReport = Pick<Project, "id" | "name" | "public">;

export interface ReportViewProps {
  games: GameForReport[];
  project: ProjectForReport;
  isPublicDisplay?: boolean;
}

export const ReportView = ({ games, project, isPublicDisplay }: ReportViewProps) => {
  const { t } = useTranslation();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const gamesList = games.map((game) => {
    return {
      value: game.id,
      label: game.name,
      kind: game.kind,
    } as SelectOption;
  });

  const [selectedGameOption, setSelectedGameOption] = useState(gamesList[0]);

  const renderSelectedGameReport = () => {
    const selectedGame = games.find((item) => item.id === selectedGameOption.value);

    if (selectedGame) {
      return <GameReport game={selectedGame} key={selectedGame.id} />;
    }
  };

  if (games.length === 0) {
    return (
      <div className={styles.noGames}>
        <Header variant="h6">{t("report:noGames")}</Header>
      </div>
    );
  }

  const totalResponses = useMemo(() => {
    return games.reduce((acc, game) => acc + game.responseCount, 0);
  }, []);

  const handleExport = async () => {
    const response = await fetch(`${API_HOST}/api/reports/projects/${project.id}`, {
      headers: {
        authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${project.name}-report.zip`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <>
      <div className={styles.container}>
        {!isPublicDisplay && (
          <MobileStickyBar
            header={t("report:shareOptions")}
            description={t("report:shareOptionsDescription")}
            button={
              <Button
                icon={<ShareIcon />}
                iconSide="left"
                className={styles.shareButton}
                onClick={() => setIsShareModalOpen(true)}
              >
                {t("report:share")}
              </Button>
            }
          />
        )}
        <div className={cx("reportContainer", { isPublicDisplay })}>
          <div className={styles.headerSection}>
            <div className={styles.headerWrapper}>
              <div className={styles.header}>
                <Header variant="h5">{project.name}</Header>
                <Paragraph color="secondary" size="small">
                  {t("report:reportDescription")}
                </Paragraph>
              </div>
              {!isPublicDisplay && (
                <ResponsiveButton
                  onClick={handleExport}
                  iconSide="left"
                  kind="outline"
                  className={styles.exportButton}
                  icon={<ArrowsDownload />}
                >
                  {t("report:export")}
                </ResponsiveButton>
              )}
            </div>
            <div className={styles.badges}>
              <ReportBadge header={t("report:totalResponses")} count={totalResponses} />
              <ReportBadge variant="blue" header={t("report:games")} count={games.length} />
            </div>
          </div>
          <Section title={t("report:selectGame")} className={styles.section}>
            <>
              <SelectInput options={gamesList} selected={selectedGameOption} onChange={setSelectedGameOption} />
              {renderSelectedGameReport()}
            </>
          </Section>
        </div>

        {!isPublicDisplay && <ShareSection project={project} />}
      </div>

      {isShareModalOpen && <ShareReportModal onClose={() => setIsShareModalOpen(false)} project={project} />}
    </>
  );
};
